import { useEffect, useState } from 'react';

import { useReactFlow } from 'reactflow';

import { BlockDataParameter } from 'types';
import useTakeSnapshot from './useTakeSnapshot';

interface UseNodeParameterParams {
  id: string;
  index: number;
  initValue?: string;
  isSelect?: boolean;
}

const useNodeParameter = ({ id, index, initValue, isSelect = false }: UseNodeParameterParams) => {
  const [value, setValue] = useState(initValue ?? '');
  const { setNodes, getNode } = useReactFlow();
  const { takeSnapshot } = useTakeSnapshot();

  const handleParameterSave = (inputValue?: string) => {
    const node = getNode(id);

    if (!node) return;

    let isChange = false;
    const parameters = node.data.parameters as BlockDataParameter[];
    const newParameters = parameters.map((parameter, i) => {
      const newParameter = { ...parameter };
      const newValue = inputValue ?? value;
      if (i === index && newParameter.value !== newValue) {
        isChange = true;
        newParameter.value = newValue;
      }
      return newParameter;
    });

    if (isChange) {
      takeSnapshot();
      setNodes((prevNodes) =>
        prevNodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                parameters: newParameters,
              },
            };
          }
          return node;
        })
      );
    }
  };

  const handleChange = (event: React.FormEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      currentTarget: { value },
    } = event;

    takeSnapshot({ undoRedo: false, updateCode: false });

    if (isSelect) {
      handleParameterSave(value);
    } else {
      setValue(value);
    }
  };

  const handleBlur = () => {
    handleParameterSave();
  };

  useEffect(() => {
    if (!isSelect) {
      setValue(initValue ?? '');
    }
  }, [initValue, isSelect, setValue]);

  return {
    value,
    onChange: handleChange,
    onBlur: handleBlur,
  };
};

export default useNodeParameter;
